/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

export enum CheckinConditionType {
  VehicleDiscovery = 1,
  Voltage = 2,
  GpsSpeed = 3
}

export enum CheckinConditionCombinator {
  And = 'AND',
  Or = 'OR'
}

export interface CheckinConditionDraftRow {
  rowId: string;
  type: CheckinConditionType;
  value: string;
  errors?: Record<string, boolean>;
}

/*
 * `rows` and `combinators` never travel apart — every consumer that holds
 * one always holds the other, so they're bundled into a single draft rather
 * than passed as two parallel arrays.
 */
export interface CheckinConditionsDraft {
  rows: CheckinConditionDraftRow[];
  combinators: CheckinConditionCombinator[];
}

export const EMPTY_CHECKIN_CONDITIONS_DRAFT: CheckinConditionsDraft = {rows: [], combinators: []};

export const CHECKIN_CONDITION_TYPES = [
  CheckinConditionType.VehicleDiscovery,
  CheckinConditionType.Voltage,
  CheckinConditionType.GpsSpeed
];

export const VEHICLE_DISCOVERY_MIN_STATE = 1;
export const VEHICLE_DISCOVERY_MAX_STATE = 4;

const WHOLE_NUMBER_PATTERN  = /^\d+$/;
const LEADING_ZEROS_PATTERN = /^0+(?=\d)/;

/*
 * Derived from the min/max bounds above rather than hand-listed, so the
 * range has one source of truth instead of two that could drift apart.
 */
export const VEHICLE_DISCOVERY_STATES = Array.from(
  {length: VEHICLE_DISCOVERY_MAX_STATE - VEHICLE_DISCOVERY_MIN_STATE + 1},
  (_value, index) => VEHICLE_DISCOVERY_MIN_STATE + index
);

/*
 * The smallest valid value for the non-negative condition types (Voltage,
 * GPS Speed — 0 is a valid reading) — distinct from VEHICLE_DISCOVERY_MIN_STATE,
 * which bounds an unrelated 1-4 state range.
 */
export const CHECKIN_CONDITION_MIN_VALUE = 0;

export const CHECKIN_CONDITION_DEFAULT_VALUES: Partial<Record<CheckinConditionType, string>> = {
  [CheckinConditionType.VehicleDiscovery] : '3',
  [CheckinConditionType.Voltage]          : '13400',
  [CheckinConditionType.GpsSpeed]         : '3'
};

/**
 * Determines which condition types are still available to select, i.e. not
 * already used by another draft row. Excluding a row's own id lets that row
 * keep showing its currently selected type as an option.
 *
 * ```typescript
 * import {getAvailableConditionTypes} from './CanRequestCheckinConditionUtils';
 *
 * getAvailableConditionTypes(rows, row.rowId);
 * ```
 *
 * @param   rows         The current check-in condition draft rows.
 * @param   excludeRowId The id of the row whose own selection should stay available.
 * @returns The condition types not used by any other row.
 */
export const getAvailableConditionTypes = (
  rows: CheckinConditionDraftRow[],
  excludeRowId?: string
): CheckinConditionType[] => {
  const usedTypes = (rows || [])
    .filter((row) => row.rowId !== excludeRowId)
    .map((row) => row.type);
  return CHECKIN_CONDITION_TYPES.filter((type) => !usedTypes.includes(type));
};

/**
 * Determines whether a check-in condition type is Vehicle Discovery — the
 * one type with device-state semantics (a 1-4 state dropdown); Voltage and
 * GPS Speed always share the same generic numeric-field behavior.
 *
 * ```typescript
 * import {isVehicleDiscoveryCondition} from './CanRequestCheckinConditionUtils';
 *
 * isVehicleDiscoveryCondition(CheckinConditionType.VehicleDiscovery);
 * ```
 *
 * @param   type The check-in condition type.
 * @returns `true` when the type is Vehicle Discovery, otherwise `false`.
 */
export const isVehicleDiscoveryCondition = (type: CheckinConditionType): boolean => (
  type === CheckinConditionType.VehicleDiscovery
);

/**
 * Determines whether a check-in condition value is valid for its type.
 * Vehicle Discovery is constrained to the device's 1-4 state range;
 * Voltage and GPS Speed accept any non-negative whole number (0 is a valid
 * reading — e.g. GPS Speed 0 for a parked vehicle).
 *
 * ```typescript
 * import {isValidCheckinConditionValue} from './CanRequestCheckinConditionUtils';
 *
 * isValidCheckinConditionValue(CheckinConditionType.GpsSpeed, '0');
 * ```
 *
 * @param   type  The check-in condition type.
 * @param   value The raw condition value.
 * @returns `true` when the value is valid for the given type, otherwise `false`.
 */
export const isValidCheckinConditionValue = (type: CheckinConditionType, value: string): boolean => {
  const trimmedValue                  = `${value ?? ''}`.trim();
  const numeric                       = Number(trimmedValue);
  const isWholeNumber                 = WHOLE_NUMBER_PATTERN.test(trimmedValue);
  const isWithinVehicleDiscoveryRange = numeric >= VEHICLE_DISCOVERY_MIN_STATE &&
    numeric <= VEHICLE_DISCOVERY_MAX_STATE;
  return isVehicleDiscoveryCondition(type)
    ? isWholeNumber && isWithinVehicleDiscoveryRange
    : isWholeNumber;
};

/**
 * Normalizes a valid check-in condition value for the wire format by
 * trimming it and stripping leading zeros, keeping the digits as text so
 * large values are sent exactly as typed (no exponent or precision loss).
 *
 * ```typescript
 * import {normalizeCheckinConditionValue} from './CanRequestCheckinConditionUtils';
 *
 * normalizeCheckinConditionValue(' 013000 ');
 * ```
 *
 * @param   value The raw, already-validated condition value.
 * @returns The value without surrounding whitespace or leading zeros.
 */
export const normalizeCheckinConditionValue = (value: string): string => (
  `${value ?? ''}`.trim().replace(LEADING_ZEROS_PATTERN, '')
);

/*
 * The user enters GPS Speed in m/s, but the LMU (firmware 4.6.3+) expects the
 * condition value in cm/s, so it is scaled by this factor on the wire.
 */
export const GPS_SPEED_CM_PER_M = 100;

/**
 * Transforms a check-in condition UI value, in the unit the user enters,
 * into the device value, in the unit the device expects: GPS Speed is
 * converted from meters per second (m/s) to centimeters per second (cm/s),
 * while every other condition type is returned unchanged.
 *
 * ```typescript
 * import {transformUiValueToDeviceValue} from './CanRequestCheckinConditionUtils';
 *
 * transformUiValueToDeviceValue(CheckinConditionType.GpsSpeed, '3');
 * // '300'
 * ```
 *
 * @param   type  The check-in condition type.
 * @param   value The normalized, user-entered condition value.
 * @returns The condition value in the unit the device expects.
 */
export const transformUiValueToDeviceValue = (type: CheckinConditionType, value: string): string => (
  type === CheckinConditionType.GpsSpeed
    ? `${Number(value) * GPS_SPEED_CM_PER_M}`
    : value
);

/**
 * Transforms a check-in condition device value, in the unit the device
 * expects, back into the UI value, in the unit the user sees, reversing
 * `transformUiValueToDeviceValue`: GPS Speed is converted from centimeters
 * per second (cm/s) to meters per second (m/s), while every other condition
 * type, and any GPS Speed value that isn't a whole number, is returned
 * unchanged.
 *
 * ```typescript
 * import {transformDeviceValueToUiValue} from './CanRequestCheckinConditionUtils';
 *
 * transformDeviceValueToUiValue(CheckinConditionType.GpsSpeed, '300');
 * // '3'
 * ```
 *
 * @param   type  The check-in condition type.
 * @param   value The condition value as sent to the device.
 * @returns The condition value in its user-facing unit.
 */
export const transformDeviceValueToUiValue = (type: CheckinConditionType, value: string): string => (
  type === CheckinConditionType.GpsSpeed && WHOLE_NUMBER_PATTERN.test(`${value ?? ''}`)
    ? `${Number(value) / GPS_SPEED_CM_PER_M}`
    : value
);

export const CHECKIN_CONDITION_REQUIRED_ERROR_KEY       = 'common:editors.shared.required';
export const CHECKIN_CONDITION_INVALID_NUMBER_ERROR_KEY = 'common:validation.errorMessages.positiveInteger';

/**
 * Resolves the translation key describing why a check-in condition value is
 * invalid: empty values are "required", while a non-empty value is only
 * ever invalid for not being a non-negative whole number (Vehicle Discovery's
 * value always comes from its constrained dropdown, so it can't reach this
 * branch with a malformed value).
 *
 * ```typescript
 * import {getCheckinConditionValueErrorKey} from './CanRequestCheckinConditionUtils';
 *
 * getCheckinConditionValueErrorKey(row);
 * ```
 *
 * @param   row The draft row to inspect.
 * @returns The translation key for the row's current validation error.
 */
export const getCheckinConditionValueErrorKey = (row: CheckinConditionDraftRow): string => (
  row.value ? CHECKIN_CONDITION_INVALID_NUMBER_ERROR_KEY : CHECKIN_CONDITION_REQUIRED_ERROR_KEY
);

/**
 * Computes row-level validation flags for a check-in condition draft row.
 *
 * ```typescript
 * import {getCheckinConditionRowErrors} from './CanRequestCheckinConditionUtils';
 *
 * getCheckinConditionRowErrors(row);
 * ```
 *
 * @param   row The draft row to validate.
 * @returns A map of field names to validation error flags.
 */
export const getCheckinConditionRowErrors = (row: CheckinConditionDraftRow): Record<string, boolean> => ({
  value : !isValidCheckinConditionValue(row.type, row.value)
});

/**
 * Applies validation to a check-in condition draft row.
 *
 * ```typescript
 * import {withCheckinConditionErrors} from './CanRequestCheckinConditionUtils';
 *
 * withCheckinConditionErrors(row);
 * ```
 *
 * @param   row The draft row to validate.
 * @returns The row with the computed `errors` map.
 */
export const withCheckinConditionErrors = (row: CheckinConditionDraftRow): CheckinConditionDraftRow => ({
  ...row,
  errors : getCheckinConditionRowErrors(row)
});

/**
 * Determines whether a check-in condition row has any validation errors present.
 *
 * ```typescript
 * import {hasCheckinConditionRowErrors} from './CanRequestCheckinConditionUtils';
 *
 * hasCheckinConditionRowErrors(row);
 * ```
 *
 * @param   row The row containing error flags.
 * @returns `true` when the row is invalid, otherwise `false`.
 */
export const hasCheckinConditionRowErrors = (row: CheckinConditionDraftRow): boolean => (
  Object.values(row.errors || {}).some(Boolean)
);

/**
 * Creates a default check-in condition draft row for the given type,
 * pre-filled with that type's default value: Vehicle Discovery defaults
 * to state 3 (VIN Lookup Started), Voltage to 13400 mV, and GPS Speed to
 * 3 m/s.
 *
 * ```typescript
 * import {createDefaultCheckinConditionRow} from './CanRequestCheckinConditionUtils';
 *
 * createDefaultCheckinConditionRow('row-1', CheckinConditionType.Voltage);
 * ```
 *
 * @param   rowId The unique row identifier.
 * @param   type  The condition type assigned to the row.
 * @returns The initialized draft row.
 */
export const createDefaultCheckinConditionRow = (
  rowId: string,
  type: CheckinConditionType
): CheckinConditionDraftRow => withCheckinConditionErrors({
  rowId,
  type,
  value : CHECKIN_CONDITION_DEFAULT_VALUES[type] || ''
});

/**
 * Serializes valid check-in condition rows into the device wire format,
 * honoring an independent AND/OR combinator per gap between rows.
 * AND binds tighter than OR: consecutive rows joined by AND are kept in the
 * same group, and a new group starts on OR, producing a DNF expression
 * (e.g. `1-4&2-13000|3-200` for `(VehicleDiscovery=4 AND Voltage=13000) OR
 * GpsSpeed=2 m/s`). GPS Speed is converted from m/s to the cm/s the device
 * expects. Invalid rows are dropped rather than sent.
 *
 * ```typescript
 * import {serializeCheckinConditions} from './CanRequestCheckinConditionUtils';
 *
 * serializeCheckinConditions(draft);
 * ```
 *
 * @param   draft The check-in conditions draft (rows and their combinators).
 * @returns The serialized condition string, or `undefined` when no valid rows exist.
 */
export const serializeCheckinConditions = (draft: CheckinConditionsDraft): string | undefined => {
  const {rows, combinators} = draft || EMPTY_CHECKIN_CONDITIONS_DRAFT;
  const groups: string[][] = [];
  (rows || []).forEach((row, index) => {
    if (isValidCheckinConditionValue(row.type, row.value)) {
      const normalizedValue  = normalizeCheckinConditionValue(row.value);
      const deviceValue      = transformUiValueToDeviceValue(row.type, normalizedValue);
      const term             = `${row.type}-${deviceValue}`;
      const combinatorBefore = index > 0 ? combinators?.[index - 1] : undefined;
      if (!groups.length || combinatorBefore === CheckinConditionCombinator.Or) {
        groups.push([term]);
      } else {
        groups[groups.length - 1].push(term);
      }
    }
  });
  return groups.length
    ? groups.map((group) => group.join('&')).join('|')
    : undefined;
};

export interface ParsedCheckinConditionTerm {
  type: CheckinConditionType;
  value: string;
}

/**
 * Parses a serialized check-in condition string back into its AND-grouped,
 * OR-separated terms, reversing `serializeCheckinConditions` (GPS Speed is
 * converted back from cm/s to m/s).
 *
 * ```typescript
 * import {parseCheckinConditions} from './CanRequestCheckinConditionUtils';
 *
 * parseCheckinConditions('2-13400&3-300|1-4');
 * ```
 *
 * @param   condition The serialized condition string.
 * @returns The parsed AND-groups of condition terms, or an empty list when absent.
 */
export const parseCheckinConditions = (condition?: string): ParsedCheckinConditionTerm[][] => (
  condition
    ? condition.split('|').map((group) => (
      group.split('&').map((term) => {
        const [type, value] = term.split('-');
        const conditionType = Number(type) as CheckinConditionType;
        return {type: conditionType, value: transformDeviceValueToUiValue(conditionType, value)};
      })
    ))
    : []
);

export interface CheckinConditionDisplayLabels {
  conditionTypeLabels: Record<CheckinConditionType, string>;
  conditionValueUnits: Partial<Record<CheckinConditionType, string>>;
  vehicleDiscoveryStates: Record<string, string>;
  and: string;
  or: string;
}

/**
 * Builds a map of condition type to a translated label, given each type's
 * locale key within `labels`. Shared by every "one label per condition
 * type" mapping this feature needs (option labels, value field labels,
 * units), so the lookup-with-fallback logic exists in one place.
 *
 * ```typescript
 * import {mapConditionTypesToLabels} from './CanRequestCheckinConditionUtils';
 *
 * mapConditionTypesToLabels(labels, {
 *   [CheckinConditionType.Voltage]: 'voltageOption'
 * });
 * ```
 *
 * @param   labels     The translated `checkinConditions` locale labels.
 * @param   keysByType The locale key within `labels` for each condition type.
 * @returns A map of condition type to its translated label, defaulting to `''`.
 */
export const mapConditionTypesToLabels = (
  labels: Record<string, any>,
  keysByType: Partial<Record<CheckinConditionType, string>>
): Partial<Record<CheckinConditionType, string>> => {
  const result: Partial<Record<CheckinConditionType, string>> = {};
  Object.keys(keysByType).forEach((typeKey) => {
    const type     = Number(typeKey) as CheckinConditionType;
    const labelKey = keysByType[type];
    result[type]   = (labelKey && labels?.[labelKey]) || '';
  });
  return result;
};

/**
 * Resolves the translated condition type label for each check-in condition
 * type, shared by the condition editor and the request summary display.
 *
 * ```typescript
 * import {getCheckinConditionTypeLabels} from './CanRequestCheckinConditionUtils';
 *
 * getCheckinConditionTypeLabels(labels);
 * ```
 *
 * @param   labels The translated `checkinConditions` locale labels.
 * @returns A map of condition type to its translated label.
 */
export const getCheckinConditionTypeLabels = (
  labels: Record<string, any>
): Record<CheckinConditionType, string> => (
  mapConditionTypesToLabels(labels, {
    [CheckinConditionType.VehicleDiscovery] : 'vehicleDiscoveryOption',
    [CheckinConditionType.Voltage]          : 'voltageOption',
    [CheckinConditionType.GpsSpeed]         : 'gpsSpeedOption'
  }) as Record<CheckinConditionType, string>
);

/*
 * Non-breaking spaces pad the AND/OR operator so the extra visual gap
 * survives normal HTML whitespace collapsing, while the plain spaces
 * bordering it still allow the line to wrap between conditions.
 */
const CONDITION_OPERATOR_PADDING = '  ';

const withConditionOperatorSpacing = (operator: string): string => (
  ` ${CONDITION_OPERATOR_PADDING}${operator}${CONDITION_OPERATOR_PADDING} `
);

/**
 * Formats a serialized check-in condition string into a human-readable
 * summary, resolving each term's type label and value: Vehicle Discovery
 * resolves to its state label, while Voltage and GPS Speed append their
 * unit of measure. Each term's type and value stay close together, while
 * extra spacing sets the AND/OR operator apart from the conditions it joins.
 *
 * ```typescript
 * import {formatCheckinConditionsForDisplay} from './CanRequestCheckinConditionUtils';
 *
 * formatCheckinConditionsForDisplay('2-13400&3-300', labels);
 * // 'Voltage: 13400 mV   AND   GPS Speed: 3 m/s'
 * ```
 *
 * @param   condition The serialized condition string.
 * @param   labels    The translated type, unit, Vehicle Discovery state, and combinator labels.
 * @returns The human-readable condition summary, or an empty string when absent.
 */
export const formatCheckinConditionsForDisplay = (
  condition: string | undefined,
  labels: CheckinConditionDisplayLabels
): string => (
  parseCheckinConditions(condition).map((group) => (
    group.map((term) => {
      const typeLabel  = labels.conditionTypeLabels[term.type] || '';
      const unit       = labels.conditionValueUnits[term.type];
      const valueLabel = isVehicleDiscoveryCondition(term.type)
        ? (labels.vehicleDiscoveryStates[term.value] || term.value)
        : `${term.value}${unit ? ` ${unit}` : ''}`;
      return `${typeLabel}: ${valueLabel}`;
    }).join(withConditionOperatorSpacing(labels.and))
  )).join(withConditionOperatorSpacing(labels.or))
);
